import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth/context";
import { useHousehold } from "./useHousehold";
import { useMembers } from "./useMembers";
import { selfHealMember } from "@/lib/api/members";

/**
 * Self-heal brakującego / niespójnego member doc dla zalogowanego rodzica.
 *
 * Trigger: rodzic ma `users/{uid}.householdId`, ale w
 * `households/{hid}/members/{uid}`:
 *   - brak dokumentu, ALBO
 *   - role !== "parent", ALBO
 *   - jest założycielem (createdBy === uid) ale isAdmin nie jest true
 *     (legacy household sprzed permission systemu).
 * Bez naprawy rule isParent zwraca false (pierwsze dwa) lub admin-only
 * sekcje UI flickerują (trzeci, bo klient nie może sam ustawić isAdmin
 * przez self-edit path).
 *
 * Implementacja: wołamy Cloud Function `selfHealMember` (Admin SDK omija
 * Firestore rules, klient sam nie mógłby zmienić własnego role/isAdmin
 * w istniejącym docu). Funkcja idempotentna — gdy member doc jest OK,
 * no-op.
 *
 * Strategie wyzwalania:
 *   - members.find(uid) === undefined po załadowaniu listy → brak doc
 *     (lub doc istnieje ale memberSchema.safeParse go odrzuca; w obu
 *     przypadkach wywołujemy heal)
 *   - Tylko raz na sesję (refguard); w razie błędu sieci dopuszczamy
 *     ponowną próbę.
 */
export function useMemberSelfHeal(): void {
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const uid = firebaseUser?.uid;
  const { household, loading: householdLoading } = useHousehold(hid);
  const { members, loading: membersLoading } = useMembers(hid);
  const triedRef = useRef(false);

  useEffect(() => {
    if (triedRef.current) return;
    if (!hid || !uid || !firebaseUser) return;
    if (userDoc?.role !== "parent") return;
    if (householdLoading || membersLoading) return;
    if (!household) return;
    const me = members.find((m) => m.userId === uid);
    const isCreator = household.createdBy === uid;
    // Heal kiedy: brak doc, ALBO doc istnieje ale role !== parent,
    // ALBO założyciel bez isAdmin=true (backfill flagi). Wszystkie trzy
    // przypadki wymagają Admin SDK — klient nie może zmienić własnego
    // role/isAdmin (rule update blokuje, a setDoc({isAdmin:true},merge)
    // wpada w pętlę optymistyczny-update / rollback / re-render).
    const okRole = !!me && me.role === "parent";
    const okAdmin = !isCreator || me?.isAdmin === true;
    if (okRole && okAdmin) return;

    triedRef.current = true;
    void selfHealMember().catch((e) => {
      console.warn("[useMemberSelfHeal] failed:", e);
      triedRef.current = false;
    });
  }, [
    hid,
    uid,
    firebaseUser,
    userDoc?.role,
    household,
    householdLoading,
    members,
    membersLoading,
  ]);
}
